import { useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { ProjectCell } from '../../components/common/project-cell';
import ScreenHeader from '../../components/common/screen-header';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { partnersService } from '../../services/partners.service';
import { projectsService } from '../../services/projects.service';

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs font-medium text-ink-muted uppercase tracking-wide">{label}</span>
    <span className="text-sm text-ink">{value ?? '-'}</span>
  </div>
);

const PartnerView = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const canUpdate = useCanUpdate(JOB_NAMES.PARTNERS);

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partners', id],
    queryFn: () => partnersService.getById(id!),
    enabled: !!id,
  });

  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  useBreadcrumbTitle(partner?.name || null);

  const isCustomer = useMemo(() => partner?.partnerCategory === 'CUSTOMER', [partner]);

  if (isLoading) return <PageSkeleton />;
  if (!partner) return null;

  return (
    <PermissionGuard jobName={JOB_NAMES.PARTNERS}>
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-Partner-View">
        {/* Header */}
        <ScreenHeader
          icon="pi pi-handshake"
          title={partner.name || 'Partner'}
          subtitle={`${isCustomer ? 'Customer' : 'Internal'} partner`}
          onBack={() => navigate('/partners')}
          right={canUpdate && (
            <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => navigate(`/partners/${id}/edit`)} />
          )}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Partner Information */}
          <div className="ds-card p-4">
            <h2 className="text-sm font-semibold text-ink mb-3">Partner Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Name" value={partner.name || '-'} />
              <Field label="Category" value={isCustomer ? 'Customer' : 'Internal'} />
              <Field label="Type" value={partner.partnerType || '-'} />
              {!isCustomer && <Field label="Project" value={<ProjectCell appId={partner.appId} projects={accessibleProjects} />} />}
            </div>
          </div>

          {/* Contact */}
          <div className="ds-card p-4">
            <h2 className="text-sm font-semibold text-ink mb-3">Contact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Contact Person" value={partner.contactPerson || '-'} />
              <Field label="Contact Phone" value={partner.contactPhoneNumber || '-'} />
              <Field label="Contact Role" value={partner.contactPersonRole || '-'} />
              <Field
                label="Website"
                value={
                  partner.website ? (
                    <a href={partner.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {partner.website}
                    </a>
                  ) : (
                    '-'
                  )
                }
              />
              <div className="sm:col-span-2">
                <Field label="Address" value={partner.address || '-'} />
              </div>
            </div>
          </div>

          <AuditInfoCard
            className="lg:col-span-2"
            createdAt={partner.createdAt}
            updatedAt={partner.updatedAt}
            createdBy={partner.createdBy}
            updatedBy={partner.updatedBy}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default PartnerView;
